import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Grid,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { Link } from "react-router-dom";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import FacebookIcon from "@mui/icons-material/Facebook";
import LoginIcon from "@mui/icons-material/Login";
import LogoutIcon from "@mui/icons-material/Logout";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import ArticleIcon from "@mui/icons-material/Article";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import RamenDiningIcon from "@mui/icons-material/RamenDining";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import VerifiedIcon from "@mui/icons-material/Verified";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import LanguageIcon from "@mui/icons-material/Language";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";

import API from "../../services/Config";
import { AuthContext } from "../../context/AuthContext";
import { COLORS } from "../../styles/base/colors";
import {
  buildBrandGradient,
  getBrandColors,
  readableAccent,
  readableTextOn,
  withAlpha,
} from "../../utils/gradients";
import ArticlesShowcase, {
  ArticleDialog,
  ensureArrayValue,
} from "./ProfileArticlesSection";
import DishesShowcase, { DishDialog } from "./ProfileDishesSection";

const resolveSubdomain = () => {
  if (typeof window === "undefined") return "";
  const params = new URLSearchParams(window.location.search);
  const fromQuery = params.get("subdomain");
  if (fromQuery && fromQuery.trim()) return fromQuery.trim().toLowerCase();
  const host = window.location.hostname || "";
  if (!host || host === "localhost" || host.startsWith("127.")) return "";
  const parts = host.split(".");
  return parts.length ? parts[0].toLowerCase() : "";
};

const ensureArray = (value) => (Array.isArray(value) ? value : []);

const toTagArray = (value) => {
  if (Array.isArray(value))
    return value
      .filter(Boolean)
      .map((tag) => `${tag}`.trim())
      .filter(Boolean);
  if (typeof value === "string" && value.trim()) {
    return value
      .split(",")
      .map((tag) => tag.trim())
      .filter(Boolean);
  }
  return [];
};

const COMMUNITY_LEVELS = ["COMUNIDAD", "COMUNIDAD_Y_PACIENTES"];
const PATIENT_LEVEL = "COMUNIDAD_Y_PACIENTES";

const isArticleVisibleForCommunity = (article) => {
  if (!article) return false;
  const approved =
    article?.estatusPublicacion?.aprobado === true &&
    article?.estatusPublicacion?.rechazado !== true;
  const nivelRaw = article?.nivelArticulo;
  const nivel =
    typeof nivelRaw === "string" ? nivelRaw.toUpperCase() : nivelRaw || "";
  return approved && COMMUNITY_LEVELS.includes(nivel);
};

const matchesPatientLevel = (article) => {
  if (!article) return false;
  if (article?.estatusPublicacion?.rechazado) return false;
  const nivelRaw = article?.nivelArticulo;
  const nivel =
    typeof nivelRaw === "string" ? nivelRaw.toUpperCase() : nivelRaw || "";
  return nivel === PATIENT_LEVEL;
};

const getValueByPath = (obj, path) => {
  if (!obj || !path) return undefined;
  return path
    .split(".")
    .reduce((acc, key) => (acc ? acc[key] : undefined), obj);
};

const pickCollection = (source, paths) => {
  if (!source) return [];
  for (const path of paths) {
    const value = getValueByPath(source, path);
    if (Array.isArray(value) && value.length) return value;
  }
  for (const path of paths) {
    const value = getValueByPath(source, path);
    if (Array.isArray(value)) return value;
  }
  return [];
};

const mapResourceItems = (items, fallbackLabel) =>
  ensureArray(items).map((item, index) => {
    if (item && typeof item === "string") {
      return {
        id: `${fallbackLabel}-${index}`,
        title: item,
        description: "",
        image: "",
        duration: "",
        difficulty: "",
        category: "",
        tags: [],
        url: "",
      };
    }

    const title =
      item?.titulo ||
      item?.title ||
      item?.nombre ||
      item?.name ||
      `${fallbackLabel} ${index + 1}`;
    const description =
      item?.descripcion ||
      item?.description ||
      item?.resumen ||
      item?.detalle ||
      item?.notes ||
      "";
    const image =
      item?.imagen || item?.image || item?.cover || item?.thumbnail || "";
    const duration =
      item?.duracion || item?.tiempo || item?.semanas || item?.dias || "";
    const difficulty = item?.dificultad || item?.nivel || "";
    const category = item?.categoria || item?.tipo || item?.objetivo || "";
    const tags = toTagArray(item?.etiquetas || item?.tags);
    const url = item?.url || item?.link || item?.cta || "";

    return {
      id: item?.id || item?._id || `${fallbackLabel}-${index}`,
      title,
      description,
      image,
      duration,
      difficulty,
      category,
      tags,
      url,
    };
  });

const DEFAULT_SECTION = "articles";
const DISH_PAGE_SIZE = 8;

const StarMeter = ({ value = 0, size = 28 }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
    {[0, 1, 2, 3, 4].map((idx) => {
      const fill = Math.min(Math.max(value - idx, 0), 1);
      return (
        <Box
          key={`profile-star-${idx}`}
          sx={{ position: "relative", width: size, height: size }}
        >
          <StarBorderIcon sx={{ fontSize: size, color: "#cbd5f5" }} />
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              overflow: "hidden",
              width: `${fill * 100}%`,
            }}
          >
            <StarIcon
              sx={{
                fontSize: size,
                color: "#facc15",
                filter: "drop-shadow(0 2px 6px rgba(250,204,21,0.5))",
              }}
            />
          </Box>
        </Box>
      );
    })}
  </Box>
);

const NutriologoProfile = () => {
  const {
    isAuthenticated: isPatientAuthenticated,
    user: patientUser,
    logout: logoutPatient,
  } = useContext(AuthContext) || {};
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [nutriologo, setNutriologo] = useState(null);
  const [activeSection, setActiveSection] = useState(DEFAULT_SECTION);
  const [authorArticles, setAuthorArticles] = useState([]);
  const [articlesLoading, setArticlesLoading] = useState(false);
  const [articleSearch, setArticleSearch] = useState("");
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [articleDialogOpen, setArticleDialogOpen] = useState(false);
  const [articleDialogLoading, setArticleDialogLoading] = useState(false);
  const [likePendingId, setLikePendingId] = useState(null);
  // Platillos compartidos (endpoint público paginado por subdominio)
  const [dishes, setDishes] = useState([]);
  const [dishesTotal, setDishesTotal] = useState(0);
  // Total sin filtro de búsqueda (para menú y estadísticas)
  const [dishesBaseTotal, setDishesBaseTotal] = useState(0);
  const [dishesLoading, setDishesLoading] = useState(true);
  const [dishSearch, setDishSearch] = useState("");
  const [dishPage, setDishPage] = useState(1);
  const [selectedDish, setSelectedDish] = useState(null);
  const [dishDialogOpen, setDishDialogOpen] = useState(false);
  const [dishDetailLoading, setDishDetailLoading] = useState(false);

  const patientLikeId =
    patientUser?.paciente?._id || patientUser?._id || patientUser?.id || "";
  const canLikeArticles = Boolean(isPatientAuthenticated && patientLikeId);
  const handlePatientLogout = useCallback(() => {
    if (typeof logoutPatient === "function") {
      logoutPatient();
    }
  }, [logoutPatient]);

  useEffect(() => {
    const fetchInfo = async () => {
      setLoading(true);
      setError(null);
      try {
        const subdomain = resolveSubdomain();
        if (!subdomain) {
          throw new Error("No se detectó ningún subdominio válido.");
        }
        const response = await fetch(
          `/api/nutriologo/subdomain/info?subdominio=${subdomain}`
        );
        if (!response.ok) {
          throw new Error("No se encontró información del nutriólogo.");
        }
        const data = await response.json();
        if (!data?.nutriologoInfo) {
          throw new Error("No se encontró información del nutriólogo.");
        }
        setNutriologo(data.nutriologoInfo);
      } catch (err) {
        setError(
          err.message || "No se pudo cargar la información del nutriólogo."
        );
      } finally {
        setLoading(false);
      }
    };

    fetchInfo();
  }, []);

  const displayName = useMemo(() => {
    if (!nutriologo) return "";
    const custom = nutriologo.nombreAMostrar || nutriologo.nombreAmMostrar;
    if (custom && custom.trim()) return custom.trim();
    const userName = nutriologo.usuario?.nombre || nutriologo.nombre || "";
    const userLast =
      nutriologo.usuario?.apellidos || nutriologo.apellidos || "";
    return `${userName} ${userLast}`.trim() || "Nutriólogo";
  }, [nutriologo]);

  const avatarRaw = nutriologo?.usuario?.imagen || nutriologo?.imagen || "";
  const avatarUrl = useMemo(() => {
    if (!avatarRaw) return "";
    const trimmed = `${avatarRaw}`.trim();
    if (!trimmed || trimmed.toLowerCase() === "default") return "";
    return trimmed;
  }, [avatarRaw]);
  const subdomainTag =
    nutriologo?.subdomain || nutriologo?.usuario?.subdominio || "";
  const phoneDisplay = nutriologo?.telefonoAMostrar?.trim();
  const facebookUrl = nutriologo?.urlFacebook?.trim();
  const addressDisplay = nutriologo?.direccionAMostrar?.trim();
  const stateDisplay = nutriologo?.estado?.trim();
  const specialtyDisplay =
    nutriologo?.especialidadAMostrar?.trim() ||
    nutriologo?.especialidad?.trim();
  const headlineDisplay =
    nutriologo?.fraseAMostrar?.trim() ||
    nutriologo?.descripcionCorta?.trim() ||
    "Bienestar diseñado para ti.";
  const ratingSummary = useMemo(() => {
    if (!nutriologo?.calificacion) return null;
    const raw = nutriologo.calificacion;
    if (raw?.data && typeof raw.data === "object") {
      if (
        typeof raw.data.promedio === "number" ||
        typeof raw.data.total === "number"
      ) {
        return raw.data;
      }
      if (raw.data?.data && typeof raw.data.data === "object") {
        return raw.data.data;
      }
    }
    if (typeof raw?.promedio === "number" || typeof raw?.total === "number") {
      return raw;
    }
    return null;
  }, [nutriologo?.calificacion]);

  const ratingTotal = ratingSummary ? Number(ratingSummary.total) || 0 : 0;
  const ratingHasMinReviews = ratingTotal >= 5;
  const ratingAverage = Number(ratingSummary?.promedio) || 0;
  const ratingDisplayAverage = ratingHasMinReviews ? ratingAverage : 0;

  const defaultAuthor = useMemo(
    () => ({
      name: displayName || "Nutriólogo",
      avatar: avatarUrl || "",
    }),
    [displayName, avatarUrl]
  );

  // Colores de marca del nutriólogo (color1/2/3 del backend).
  // El degradado usa SOLO color1 -> color2 (interpolado en HSL para evitar
  // tonos sucios); color3 se usa como acento en el resto del diseño.
  const brandColors = useMemo(() => getBrandColors(nutriologo || {}), [
    nutriologo,
  ]);
  const [brand1, brand2, brand3] = useMemo(() => {
    const [a, b = brandColors[0], c = brandColors[brandColors.length - 1]] =
      brandColors;
    return [a, b, c];
  }, [brandColors]);
  const gradient = useMemo(() => buildBrandGradient(brandColors), [
    brandColors,
  ]);
  const textOnBrand = useMemo(
    () => readableTextOn(brandColors.slice(0, 2)),
    [brandColors]
  );
  const accentPrimary = useMemo(() => readableAccent(brand1), [brand1]);
  const accentSecondary = useMemo(() => readableAccent(brand2), [brand2]);
  const accentTertiary = useMemo(() => readableAccent(brand3), [brand3]);
  // Degradado para texto sobre fondos claros (versión legible de los 2 colores)
  const titleGradient = useMemo(
    () => buildBrandGradient([accentPrimary, accentSecondary], { angle: 100 }),
    [accentPrimary, accentSecondary]
  );

  const handleOpenLink = (url) => {
    if (!url) return;
    const formatted = url.startsWith("http") ? url : `https://${url}`;
    window.open(formatted, "_blank", "noopener,noreferrer");
  };

  const nutriologoId = nutriologo?._id || nutriologo?.id || "";

  const sharedArticleIds = useMemo(() => {
    const raw = nutriologo?.blog?.articulosCompartidos || [];
    const ids = raw
      .map((entry) => (typeof entry === "object" ? entry?._id : entry))
      .filter(Boolean);
    return Array.from(new Set(ids));
  }, [nutriologo?.blog?.articulosCompartidos]);

  useEffect(() => {
    if (!nutriologoId) return;
    let active = true;

    const fetchArticlesByAuthor = async () => {
      setArticlesLoading(true);
      try {
        const { data: payload } = await API.get(
          `/blogcomunidad/articulos/autor/${nutriologoId}`
        );
        if (!active) return;
        const baseArticles = Array.isArray(payload)
          ? payload
          : Array.isArray(payload?.data)
          ? payload.data
          : Array.isArray(payload?.articulos)
          ? payload.articulos
          : [];

        const ownArticles = baseArticles
          .map((article) => ({ ...article, esCompartido: false }))
          .filter(matchesPatientLevel);

        let sharedArticles = [];
        if (sharedArticleIds.length) {
          const sharedResponses = await Promise.all(
            sharedArticleIds.map((id) =>
              API.get(`/blogcomunidad/articulos/${id}`)
                .then((res) =>
                  res.data ? { ...res.data, esCompartido: true } : null
                )
                .catch(() => null)
            )
          );
          if (!active) return;
          sharedArticles = sharedResponses
            .filter(Boolean)
            .filter(isArticleVisibleForCommunity);
        }

        const combined = [...ownArticles, ...sharedArticles];
        const deduped = [];
        const seen = new Set();
        combined.forEach((article) => {
          const key = article?._id || article?.id;
          if (key && seen.has(key)) return;
          if (key) seen.add(key);
          deduped.push(article);
        });

        if (active) {
          setAuthorArticles(deduped);
        }
      } catch (fetchError) {
        if (!active) return;
        console.warn(
          `[NutriologoProfile] Error al cargar artículos del nutriólogo ${nutriologoId}:`,
          fetchError
        );
        setAuthorArticles([]);
      } finally {
        if (active) {
          setArticlesLoading(false);
        }
      }
    };

    setAuthorArticles([]);
    fetchArticlesByAuthor();

    return () => {
      active = false;
    };
  }, [nutriologoId, sharedArticleIds]);

  const fallbackArticleItems = useMemo(
    () =>
      mapResourceItems(
        pickCollection(nutriologo, [
          "articulos",
          "contenido.articulos",
          "resources.articles",
          "blog.articulos",
          "blogPosts",
        ]),
        "Artículo"
      ),
    [nutriologo]
  );

  const remoteArticleItems = useMemo(
    () => mapResourceItems(authorArticles, "Artículo"),
    [authorArticles]
  );

  const articleResourceItems = useMemo(
    () =>
      remoteArticleItems.length ? remoteArticleItems : fallbackArticleItems,
    [remoteArticleItems, fallbackArticleItems]
  );

  const articlesForShowcase = useMemo(() => {
    if (authorArticles.length) return authorArticles;
    const fallbackTimestamp =
      nutriologo?.updatedAt ||
      nutriologo?.createdAt ||
      nutriologo?.usuario?.createdAt ||
      new Date().toISOString();
    return fallbackArticleItems.map((item, index) => ({
      _id: `fallback-article-${index}`,
      titulo: item.title,
      descripcion: item.description,
      articulo: item.description,
      imagenUrl: item.image,
      numeroLikes: 0,
      likedBy: [],
      comentarios: [],
      createdAt: fallbackTimestamp,
      autorNombre: defaultAuthor.name,
      autorImagen: defaultAuthor.avatar,
      autorId: {
        nombreAMostrar: defaultAuthor.name,
        usuario: {
          nombre: nutriologo?.usuario?.nombre || defaultAuthor.name,
          apellidos: nutriologo?.usuario?.apellidos || "",
          imagen: defaultAuthor.avatar,
        },
      },
      isFallback: true,
    }));
  }, [
    authorArticles,
    fallbackArticleItems,
    defaultAuthor.name,
    defaultAuthor.avatar,
    nutriologo?.usuario?.nombre,
    nutriologo?.usuario?.apellidos,
    nutriologo?.usuario?.createdAt,
    nutriologo?.createdAt,
    nutriologo?.updatedAt,
  ]);

  // Platillos compartidos: se consultan al endpoint público por subdominio,
  // con paginación y búsqueda del lado del servidor (el catálogo puede ser
  // muy grande). El subdominio canónico viene del propio perfil.
  const profileSubdomain = subdomainTag || resolveSubdomain();

  useEffect(() => {
    if (!nutriologoId || !profileSubdomain) return;
    let active = true;
    setDishesLoading(true);

    const fetchDishes = async () => {
      try {
        const { data } = await API.get(
          `/platillos/publicos/subdominio/${encodeURIComponent(
            profileSubdomain
          )}`,
          {
            params: {
              skip: (dishPage - 1) * DISH_PAGE_SIZE,
              limit: DISH_PAGE_SIZE,
              search: dishSearch.trim() || undefined,
            },
          }
        );
        if (!active) return;
        setDishes(Array.isArray(data?.platillos) ? data.platillos : []);
        setDishesTotal(Number(data?.total) || 0);
        if (!dishSearch.trim()) {
          setDishesBaseTotal(Number(data?.total) || 0);
        }
      } catch (dishError) {
        if (!active) return;
        console.warn(
          `[NutriologoProfile] Error al cargar platillos de ${profileSubdomain}:`,
          dishError
        );
        setDishes([]);
        setDishesTotal(0);
      } finally {
        if (active) {
          setDishesLoading(false);
        }
      }
    };

    // Pequeño debounce solo mientras se teclea la búsqueda
    const timer = setTimeout(fetchDishes, dishSearch.trim() ? 350 : 0);

    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [nutriologoId, profileSubdomain, dishPage, dishSearch]);

  const handleDishSearchChange = useCallback((value) => {
    setDishSearch(value || "");
    setDishPage(1);
  }, []);

  const handleOpenDish = useCallback(
    async (dish) => {
      if (!dish) return;
      setSelectedDish(dish);
      setDishDialogOpen(true);
      const dishId = dish?._id || dish?.id;
      if (!dishId || !profileSubdomain) return;

      setDishDetailLoading(true);
      try {
        const { data } = await API.get(
          `/platillos/publicos/subdominio/${encodeURIComponent(
            profileSubdomain
          )}/${dishId}`
        );
        setSelectedDish(data || dish);
      } catch (detailError) {
        console.error(
          `[NutriologoProfile] Error al obtener el platillo ${dishId}:`,
          detailError
        );
      } finally {
        setDishDetailLoading(false);
      }
    },
    [profileSubdomain]
  );

  const handleCloseDish = useCallback(() => {
    setDishDialogOpen(false);
    setSelectedDish(null);
  }, []);

  const sectionDefinitions = useMemo(
    () => [
      {
        id: "articles",
        label: "Artículos",
        title: "Artículos y guías",
        Icon: MenuBookIcon,
        accent: accentPrimary,
        count: articleResourceItems.length,
        emptyText: "Aún no hay artículos publicados por este nutriólogo.",
      },
      {
        id: "dishes",
        label: "Platillos",
        title: "Platillos y recetas exclusivas",
        Icon: RamenDiningIcon,
        accent: accentTertiary,
        count: dishesBaseTotal,
        emptyText:
          "Aún no hay platillos publicados, pero pronto habrá novedades.",
      },
    ],
    [
      articleResourceItems.length,
      dishesBaseTotal,
      accentPrimary,
      accentTertiary,
    ]
  );

  useEffect(() => {
    if (!sectionDefinitions.length) return;
    const exists = sectionDefinitions.some(
      (section) => section.id === activeSection
    );
    if (!exists) {
      setActiveSection(DEFAULT_SECTION);
    }
  }, [sectionDefinitions, activeSection]);

  const stats = sectionDefinitions.map((section) => ({
    id: section.id,
    label: section.label,
    count: section.count,
    accent: section.count ? section.accent : "#94a3b8",
    icon: <section.Icon fontSize="small" />,
  }));

  const activeDefinition = useMemo(() => {
    if (!sectionDefinitions.length) return null;
    return (
      sectionDefinitions.find((section) => section.id === activeSection) ||
      sectionDefinitions.find((section) => section.id === DEFAULT_SECTION) ||
      sectionDefinitions[0]
    );
  }, [sectionDefinitions, activeSection]);

  const handleArticleSearchChange = useCallback((value) => {
    setArticleSearch(value || "");
  }, []);

  const handleOpenArticle = useCallback(
    async (article, focusComments = false) => {
      if (!article) return;
      setSelectedArticle(article);
      setArticleDialogOpen(true);
      if (article.isFallback || !article._id) {
        setArticleDialogLoading(false);
        if (focusComments) {
          setTimeout(() => {
            const container = document.getElementById("article-comments-panel");
            container?.scrollIntoView({ behavior: "smooth", block: "start" });
          }, 320);
        }
        return;
      }

      setArticleDialogLoading(true);
      try {
        const { data } = await API.get(
          `/blogcomunidad/articulos/${article._id}`
        );
        setSelectedArticle(data || article);
        if (focusComments) {
          setTimeout(() => {
            const container = document.getElementById("article-comments-panel");
            container?.scrollIntoView({
              behavior: "smooth",
              block: "start",
            });
          }, 320);
        }
      } catch (detailError) {
        console.error(
          `[NutriologoProfile] Error al obtener el artículo ${article._id}:`,
          detailError
        );
      } finally {
        setArticleDialogLoading(false);
      }
    },
    []
  );

  const handleCloseArticle = useCallback(() => {
    setArticleDialogOpen(false);
    setSelectedArticle(null);
  }, []);

  const handleToggleLike = useCallback(
    async (article, isCurrentlyLiked) => {
      if (!canLikeArticles || !patientLikeId) return;
      if (!article || article.isFallback || !article._id) return;
      const articleId = article._id;
      setLikePendingId(articleId);

      try {
        const endpoint = isCurrentlyLiked ? "unlike" : "like";
        await API.post(`/blogcomunidad/articulos/${articleId}/${endpoint}`, {
          nutriologoId: patientLikeId,
        });

        const applyMutation = (entry) => {
          if (!entry || (entry?._id || entry?.id) !== articleId) return entry;
          const likedBy = ensureArrayValue(entry?.likedBy).map((id) => `${id}`);
          let updatedLikedBy = likedBy;
          if (isCurrentlyLiked) {
            updatedLikedBy = likedBy.filter((id) => id !== `${patientLikeId}`);
          } else if (!likedBy.includes(`${patientLikeId}`)) {
            updatedLikedBy = [...likedBy, `${patientLikeId}`];
          }
          const baseLikes =
            typeof entry?.numeroLikes === "number"
              ? entry.numeroLikes
              : likedBy.length;
          return {
            ...entry,
            numeroLikes: Math.max(0, baseLikes + (isCurrentlyLiked ? -1 : 1)),
            likedBy: updatedLikedBy,
          };
        };

        setAuthorArticles((prev) => prev.map((item) => applyMutation(item)));
        setSelectedArticle((prev) => applyMutation(prev));
      } catch (likeError) {
        console.error(
          `[NutriologoProfile] Error al procesar la reacción del artículo ${articleId}:`,
          likeError
        );
      } finally {
        setLikePendingId(null);
      }
    },
    [canLikeArticles, patientLikeId]
  );

  // Entrada suave de las tarjetas (efecto premium, no altera el layout)
  const fadeUp = (delay = 0) => ({
    animation: `profileFadeUp 640ms cubic-bezier(0.16, 1, 0.3, 1) ${delay}ms both`,
    "@keyframes profileFadeUp": {
      from: { opacity: 0, transform: "translateY(22px)" },
      to: { opacity: 1, transform: "translateY(0)" },
    },
  });

  const floatingGlowSx = (color, size, position, animationName, duration) => ({
    position: "absolute",
    width: size,
    height: size,
    borderRadius: "50%",
    background: `radial-gradient(circle, ${withAlpha(color, 0.1)} 0%, transparent 70%)`,
    filter: "blur(90px)",
    pointerEvents: "none",
    animation: `${animationName} ${duration}s ease-in-out infinite alternate`,
    ...position,
    [`@keyframes ${animationName}`]: {
      from: { transform: "translate3d(0, 0, 0) scale(1)" },
      to: { transform: "translate3d(44px, 30px, 0) scale(1.08)" },
    },
  });

  return (
    <Box
      sx={{
        minHeight: "100vh",
        // Lienzo premium: base casi blanca con un degradado vertical apenas
        // perceptible; el color de marca vive en capas decorativas sutiles.
        backgroundColor: "#f7f8fc",
        backgroundImage:
          "linear-gradient(180deg, #fcfdff 0%, #f6f8fc 42%, #f1f4fa 100%)",
        py: { xs: 6, md: 8 },
        px: { xs: 2, md: 4 },
        width: "100%",
        position: "relative",
      }}
    >
      {/* Fondo decorativo: listón de marca, halo de luz, retícula de puntos
          y destellos animados. Va en un contenedor propio con overflow
          hidden para no romper el sticky del sidebar. */}
      <Box
        aria-hidden
        sx={{
          position: "absolute",
          inset: 0,
          overflow: "hidden",
          pointerEvents: "none",
        }}
      >
        {/* Listón degradado de marca en el borde superior */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: 4,
            backgroundImage: gradient,
            zIndex: 2,
          }}
        />
        {/* Halo de luz central detrás del encabezado */}
        <Box
          sx={{
            position: "absolute",
            top: -340,
            left: "50%",
            transform: "translateX(-50%)",
            width: { xs: 920, md: 1460 },
            height: 660,
            background: `radial-gradient(closest-side, ${withAlpha(
              brand1,
              0.16
            )} 0%, ${withAlpha(brand2, 0.08)} 48%, transparent 75%)`,
            filter: "blur(58px)",
          }}
        />
        {/* Destellos laterales en movimiento, muy difusos */}
        <Box
          sx={floatingGlowSx(
            brand2,
            540,
            { top: -160, left: "-12%" },
            "profileGlowA",
            30
          )}
        />
        <Box
          sx={floatingGlowSx(
            brand3,
            580,
            { top: 40, right: "-14%" },
            "profileGlowB",
            36
          )}
        />
        {/* Retícula de puntos que se desvanece hacia abajo */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: 860,
            backgroundImage:
              "radial-gradient(rgba(71,85,105,0.16) 1px, transparent 1.5px)",
            backgroundSize: "22px 22px",
            opacity: 0.45,
            maskImage:
              "linear-gradient(180deg, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0.35) 55%, transparent 100%)",
            WebkitMaskImage:
              "linear-gradient(180deg, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0.35) 55%, transparent 100%)",
          }}
        />
      </Box>
      <Container
        maxWidth={false}
        disableGutters
        sx={{ width: "100%", position: "relative", zIndex: 1 }}
      >
        {loading ? (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            gap={2}
          >
            <CircularProgress sx={{ color: accentPrimary }} />
            <Typography color="text.secondary">Cargando perfil...</Typography>
          </Box>
        ) : error ? (
          <Box
            textAlign="center"
            bgcolor="white"
            p={4}
            borderRadius={4}
            boxShadow="0 12px 30px rgba(15,23,42,0.12)"
          >
            <Typography variant="h6" gutterBottom color="error.main">
              {error}
            </Typography>
            <Typography color="text.secondary">
              Verifica la URL o contacta a tu nutriólogo.
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={4}>
            {/* Sin alignItems="flex-start" en el container: la columna
                izquierda debe estirarse a la altura de la fila para que el
                sticky del aside tenga recorrido (con flex-start nunca
                actuaba). */}
            <Grid item xs={12} md={4}>
              <Box
                component="aside"
                sx={{ position: { md: "sticky" }, top: 32 }}
              >
                <Stack spacing={3}>
                  <Box
                    sx={{
                      boxShadow: "0 24px 60px rgba(15,23,42,0.15)",
                      borderRadius: 5,
                      background: "#fff",
                      border: "1px solid rgba(15,23,42,0.05)",
                      p: { xs: 3, md: 4 },
                      ...fadeUp(0),
                    }}
                  >
                    <Stack spacing={3}>
                      <Box
                        sx={{
                          background: gradient,
                          borderRadius: 4,
                          p: 3,
                          color: textOnBrand,
                          textAlign: "center",
                          position: "relative",
                          overflow: "hidden",
                          boxShadow: `0 18px 44px ${withAlpha(brand1, 0.35)}`,
                          "&::before": {
                            content: '""',
                            position: "absolute",
                            width: 190,
                            height: 190,
                            borderRadius: "50%",
                            background: "rgba(255,255,255,0.14)",
                            top: -70,
                            right: -50,
                          },
                          "&::after": {
                            content: '""',
                            position: "absolute",
                            width: 140,
                            height: 140,
                            borderRadius: "50%",
                            background: "rgba(255,255,255,0.10)",
                            bottom: -60,
                            left: -40,
                          },
                        }}
                      >
                        {/* Destello diagonal animado */}
                        <Box
                          aria-hidden
                          sx={{
                            position: "absolute",
                            top: 0,
                            left: "-60%",
                            width: "45%",
                            height: "100%",
                            background:
                              "linear-gradient(100deg, transparent, rgba(255,255,255,0.22), transparent)",
                            transform: "skewX(-18deg)",
                            animation: "heroSheen 6s ease-in-out infinite",
                            "@keyframes heroSheen": {
                              "0%": { left: "-60%" },
                              "45%": { left: "130%" },
                              "100%": { left: "130%" },
                            },
                          }}
                        />
                        <Box sx={{ position: "relative", zIndex: 1 }}>
                          <Box
                            sx={{
                              position: "relative",
                              width: 120,
                              mx: "auto",
                              mb: 2,
                            }}
                          >
                            <Avatar
                              src={avatarUrl || undefined}
                              alt={displayName}
                              sx={{
                                width: 120,
                                height: 120,
                                fontSize: 40,
                                bgcolor: "rgba(255,255,255,0.28)",
                                border: "3px solid rgba(255,255,255,0.55)",
                                boxShadow: `0 14px 34px ${withAlpha(
                                  brand2,
                                  0.45
                                )}`,
                              }}
                            >
                              {!avatarUrl && displayName?.charAt(0)}
                            </Avatar>
                            <Box
                              sx={{
                                position: "absolute",
                                bottom: 2,
                                right: 2,
                                width: 30,
                                height: 30,
                                borderRadius: "50%",
                                display: "grid",
                                placeItems: "center",
                                background: "#fff",
                                boxShadow: "0 6px 16px rgba(15,23,42,0.28)",
                              }}
                            >
                              <VerifiedIcon
                                sx={{ fontSize: 19, color: "#3b82f6" }}
                              />
                            </Box>
                          </Box>
                          <Typography
                            variant="h5"
                            fontWeight={800}
                            gutterBottom
                          >
                            {displayName}
                          </Typography>
                          {specialtyDisplay && (
                            <Stack
                              direction="row"
                              spacing={0.75}
                              alignItems="center"
                              justifyContent="center"
                              sx={{ opacity: 0.9 }}
                            >
                              <WorkspacePremiumIcon sx={{ fontSize: 18 }} />
                              <Typography variant="subtitle1">
                                {specialtyDisplay}
                              </Typography>
                            </Stack>
                          )}
                          {subdomainTag && (
                            <Chip
                              label={`@${subdomainTag}`}
                              size="small"
                              sx={{
                                mt: 2,
                                bgcolor: "rgba(255,255,255,0.18)",
                                color: textOnBrand,
                                fontWeight: 600,
                                backdropFilter: "blur(6px)",
                              }}
                            />
                          )}
                        </Box>
                      </Box>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        textAlign="center"
                        sx={{ fontStyle: "italic" }}
                      >
                        “{headlineDisplay}”
                      </Typography>
                      <Box>
                        <Typography
                          variant="subtitle2"
                          color="text.secondary"
                          gutterBottom
                        >
                          Datos de contacto
                        </Typography>
                        <Stack direction="row" spacing={2}>
                          {phoneDisplay && (
                            <Tooltip title="Escríbeme por WhatsApp">
                              <IconButton
                                sx={{
                                  width: 50,
                                  height: 50,
                                  borderRadius: "16px",
                                  background: "#22c55e",
                                  color: "#fff",
                                  boxShadow: "0 10px 24px rgba(34,197,94,0.35)",
                                  transition: "all 220ms ease",
                                  "&:hover": {
                                    background: "#16a34a",
                                    transform: "translateY(-3px)",
                                    boxShadow:
                                      "0 14px 30px rgba(34,197,94,0.45)",
                                  },
                                }}
                                onClick={() => {
                                  const digits = phoneDisplay.replace(
                                    /\D/g,
                                    ""
                                  );
                                  handleOpenLink(`https://wa.me/${digits}`);
                                }}
                              >
                                <WhatsAppIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                          {facebookUrl && (
                            <Tooltip title="Visita mi Facebook">
                              <IconButton
                                sx={{
                                  width: 50,
                                  height: 50,
                                  borderRadius: "16px",
                                  background: "#1877f2",
                                  color: "#fff",
                                  boxShadow:
                                    "0 10px 24px rgba(24,119,242,0.35)",
                                  transition: "all 220ms ease",
                                  "&:hover": {
                                    background: "#0f62c8",
                                    transform: "translateY(-3px)",
                                    boxShadow:
                                      "0 14px 30px rgba(24,119,242,0.45)",
                                  },
                                }}
                                onClick={() => handleOpenLink(facebookUrl)}
                              >
                                <FacebookIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Stack>
                        {(addressDisplay || stateDisplay) && (
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                            sx={{ mt: 2 }}
                          >
                            <LocationOnIcon
                              sx={{ fontSize: 18, color: accentPrimary }}
                            />
                            <Typography color="text.secondary">
                              {addressDisplay && <span>{addressDisplay}</span>}
                              {addressDisplay && stateDisplay && " · "}
                              {stateDisplay && <span>{stateDisplay}</span>}
                            </Typography>
                          </Stack>
                        )}
                        {subdomainTag && (
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                            sx={{ mt: 0.75 }}
                          >
                            <LanguageIcon
                              sx={{ fontSize: 17, color: accentPrimary }}
                            />
                            <Typography
                              color="text.secondary"
                              sx={{ fontSize: 13 }}
                            >
                              {subdomainTag}.nutrilander.com
                            </Typography>
                          </Stack>
                        )}
                        <Box
                          sx={{
                            mt: 3,
                            p: 2.5,
                            borderRadius: 3,
                            border: `1px solid ${withAlpha(brand1, 0.16)}`,
                            background: `linear-gradient(145deg, ${withAlpha(
                              brand1,
                              0.06
                            )}, #fff)`,
                          }}
                        >
                          <Typography
                            variant="subtitle2"
                            color="text.secondary"
                          >
                            Calificación de pacientes
                          </Typography>
                          <Stack
                            direction="row"
                            spacing={2.5}
                            alignItems="center"
                            mt={1.5}
                          >
                            {ratingSummary && ratingHasMinReviews && (
                              <Box sx={{ minWidth: 160 }}>
                                <Typography
                                  variant="h3"
                                  fontWeight={800}
                                  color="text.primary"
                                >
                                  {ratingAverage.toFixed(1)}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  de 5.0
                                </Typography>
                              </Box>
                            )}
                            <Stack spacing={0.75}>
                              <StarMeter
                                value={ratingDisplayAverage}
                                size={26}
                              />
                              {ratingSummary && ratingHasMinReviews ? (
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  Basado en {ratingTotal} evaluaciones
                                  verificadas
                                </Typography>
                              ) : (
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  Aún no hay evaluaciones suficientes para
                                  mostrar resultados.
                                </Typography>
                              )}
                            </Stack>
                          </Stack>
                        </Box>
                      </Box>
                    </Stack>
                  </Box>
                  <Box
                    sx={{
                      boxShadow: "0 24px 60px rgba(15,23,42,0.15)",
                      borderRadius: 5,
                      background: "#fff",
                      border: "1px solid rgba(15,23,42,0.05)",
                      p: { xs: 3, md: 4 },
                      ...fadeUp(120),
                    }}
                  >
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      sx={{ mb: 1.5 }}
                    >
                      <AutoAwesomeIcon
                        sx={{ fontSize: 18, color: accentTertiary }}
                      />
                      <Typography color="text.secondary">
                        Explora las colecciones
                      </Typography>
                    </Stack>
                    <List disablePadding>
                      {sectionDefinitions.map((section) => {
                        const selected = activeSection === section.id;
                        const resourceCount = section.count;
                        const Icon = section.Icon;
                        return (
                          <ListItemButton
                            key={section.id}
                            selected={selected}
                            onClick={() => setActiveSection(section.id)}
                            sx={{
                              mb: 1.5,
                              borderRadius: 3,
                              border: "1px solid rgba(15,23,42,0.08)",
                              transition: "all 0.3s ease",
                              background: selected
                                ? withAlpha(section.accent, 0.08)
                                : "transparent",
                              boxShadow: selected
                                ? `0 15px 35px ${withAlpha(
                                    section.accent,
                                    0.18
                                  )}`
                                : "none",
                              "&.Mui-selected": {
                                borderColor: section.accent,
                                background: withAlpha(section.accent, 0.14),
                              },
                              "&:hover": {
                                borderColor: section.accent,
                                background: withAlpha(section.accent, 0.1),
                                transform: "translateX(4px)",
                              },
                            }}
                          >
                            <ListItemIcon sx={{ minWidth: 50 }}>
                              <Box
                                sx={{
                                  width: 38,
                                  height: 38,
                                  borderRadius: 2,
                                  display: "grid",
                                  placeItems: "center",
                                  transition: "all 0.3s ease",
                                  background: selected
                                    ? withAlpha(section.accent, 0.16)
                                    : "rgba(15,23,42,0.05)",
                                  color: selected
                                    ? section.accent
                                    : "text.secondary",
                                }}
                              >
                                <Icon fontSize="small" />
                              </Box>
                            </ListItemIcon>
                            <ListItemText
                              primary={section.label}
                              secondary={
                                resourceCount
                                  ? `${resourceCount} recurso${
                                      resourceCount === 1 ? "" : "s"
                                    }`
                                  : "Sin recursos aún"
                              }
                              primaryTypographyProps={{ fontWeight: 700 }}
                              secondaryTypographyProps={{
                                color: "text.secondary",
                              }}
                            />
                            <Chip
                              label={resourceCount}
                              size="small"
                              sx={{
                                fontWeight: 600,
                                background: selected
                                  ? withAlpha(section.accent, 0.16)
                                  : "#e2e8f0",
                                color: selected
                                  ? section.accent
                                  : "text.primary",
                              }}
                            />
                            <ChevronRightIcon
                              fontSize="small"
                              sx={{
                                ml: 1,
                                color: selected
                                  ? section.accent
                                  : "text.disabled",
                                transition: "transform 0.3s ease",
                                ".MuiListItemButton-root:hover &": {
                                  transform: "translateX(3px)",
                                },
                              }}
                            />
                          </ListItemButton>
                        );
                      })}
                    </List>
                  </Box>

                  <Box
                    sx={{
                      boxShadow: "0 24px 60px rgba(15,23,42,0.15)",
                      borderRadius: 5,
                      background: "#fff",
                      border: "1px solid rgba(15,23,42,0.05)",
                      p: { xs: 3, md: 4 },
                      ...fadeUp(200),
                    }}
                  >
                    <Stack spacing={1.5}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <LoginIcon
                          sx={{ fontSize: 17, color: accentPrimary }}
                        />
                        <Typography variant="subtitle2" color="text.secondary">
                          Accesos rápidos
                        </Typography>
                      </Stack>
                      {!isPatientAuthenticated && (
                        <>
                          <Button
                            fullWidth
                            variant="contained"
                            component={Link}
                            to="/login"
                            startIcon={<LoginIcon />}
                            sx={{
                              background: gradient,
                              color: textOnBrand,
                              borderRadius: 3,
                              textTransform: "none",
                              fontWeight: 600,
                              py: 1.75,
                              position: "relative",
                              overflow: "hidden",
                              boxShadow: `0 14px 30px ${withAlpha(
                                brand1,
                                0.35
                              )}`,
                              transition:
                                "transform 240ms ease, box-shadow 240ms ease",
                              "&:hover": {
                                background: gradient,
                                transform: "translateY(-2px)",
                                boxShadow: `0 18px 38px ${withAlpha(
                                  brand2,
                                  0.42
                                )}`,
                              },
                              "&::after": {
                                content: '""',
                                position: "absolute",
                                top: 0,
                                left: "-80%",
                                width: "45%",
                                height: "100%",
                                background:
                                  "linear-gradient(100deg, transparent, rgba(255,255,255,0.4), transparent)",
                                transform: "skewX(-18deg)",
                                transition: "left 600ms ease",
                              },
                              "&:hover::after": { left: "135%" },
                            }}
                          >
                            Login de pacientes
                          </Button>
                          <Button
                            fullWidth
                            variant="outlined"
                            component={Link}
                            to="/registro"
                            startIcon={<PersonAddIcon />}
                            sx={{
                              borderRadius: 3,
                              textTransform: "none",
                              fontWeight: 600,
                              py: 1.75,
                              borderWidth: 2,
                              borderColor: accentPrimary,
                              color: accentPrimary,
                              background: "rgba(255,255,255,0.6)",
                              boxShadow:
                                "inset 0 0 0 1px rgba(255,255,255,0.25)",
                              transition: "all 0.25s ease",
                              "&:hover": {
                                borderWidth: 2,
                                borderColor: accentSecondary,
                                color: accentSecondary,
                                background: "rgba(255,255,255,0.8)",
                                transform: "translateY(-2px)",
                                boxShadow: `0 12px 30px ${withAlpha(
                                  brand1,
                                  0.22
                                )}`,
                              },
                            }}
                          >
                            Registro para pacientes
                          </Button>
                        </>
                      )}
                      {isPatientAuthenticated && (
                        <Button
                          fullWidth
                          variant="outlined"
                          component={Link}
                          to="/miperfil"
                          startIcon={<ArticleIcon />}
                          sx={{
                            borderRadius: 3,
                            textTransform: "none",
                            fontWeight: 600,
                            py: 1.75,
                            borderWidth: 2,
                            borderColor: accentPrimary || COLORS.primary,
                            color: accentPrimary || COLORS.primary,
                            background: "rgba(255,255,255,0.6)",
                            boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.25)",
                            transition: "all 0.25s ease",
                            "&:hover": {
                              borderWidth: 2,
                              borderColor: accentSecondary || COLORS.primary,
                              color: accentSecondary || COLORS.primary,
                              background: "rgba(255,255,255,0.8)",
                              transform: "translateY(-2px)",
                              boxShadow: `0 12px 30px ${withAlpha(
                                brand1,
                                0.22
                              )}`,
                            },
                          }}
                        >
                          Volver a mi perfil de paciente
                        </Button>
                      )}
                      {isPatientAuthenticated && (
                        <Button
                          fullWidth
                          variant="outlined"
                          startIcon={<LogoutIcon />}
                          onClick={handlePatientLogout}
                          sx={{
                            borderRadius: 3,
                            textTransform: "none",
                            fontWeight: 600,
                            py: 1.75,
                            borderWidth: 2,
                            borderColor: "#ef4444",
                            color: "#ef4444",
                            transition: "all 0.25s ease",
                            "&:hover": {
                              borderWidth: 2,
                              borderColor: "#dc2626",
                              color: "#dc2626",
                              background: "rgba(239,68,68,0.08)",
                              transform: "translateY(-2px)",
                            },
                          }}
                        >
                          Cerrar sesión
                        </Button>
                      )}
                    </Stack>
                  </Box>
                </Stack>
              </Box>
            </Grid>
            <Grid item xs={12} md={8}>
              <Stack spacing={4}>
                <Box
                  sx={{
                    background: "#fff",
                    borderRadius: 5,
                    p: { xs: 3, md: 4 },
                    boxShadow: "0 32px 80px rgba(15,23,42,0.15)",
                    border: "1px solid rgba(15,23,42,0.05)",
                    ...fadeUp(60),
                  }}
                >
                  <Stack spacing={2}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <AutoAwesomeIcon
                        sx={{ fontSize: 16, color: accentTertiary }}
                      />
                      <Typography
                        variant="overline"
                        sx={{
                          color: accentTertiary,
                          fontWeight: 700,
                          letterSpacing: 2,
                        }}
                      >
                        Colecciones
                      </Typography>
                    </Stack>
                    <Typography
                      variant="h4"
                      fontWeight={800}
                      color="text.primary"
                    >
                      <Box
                        component="span"
                        sx={{
                          backgroundImage: titleGradient,
                          WebkitBackgroundClip: "text",
                          backgroundClip: "text",
                          color: "transparent",
                        }}
                      >
                        {displayName}
                      </Box>{" "}
                      comparte recursos hechos a tu medida
                    </Typography>
                    <Typography color="text.secondary">
                      Explora las recetas y artículos que comparte este
                      nutriólogo. Cada recurso está diseñado para acompañarte en
                      tu transformación.
                    </Typography>
                    <Stack direction="row" spacing={2} flexWrap="wrap">
                      {stats.map((stat) => (
                        <Box
                          key={stat.id}
                          sx={{
                            flex: "1 1 150px",
                            minWidth: 150,
                            borderRadius: 3,
                            border: "1px solid rgba(15,23,42,0.08)",
                            background:
                              "linear-gradient(120deg, rgba(248,250,252,0.9), #fff)",
                            p: 2.5,
                            transition: "all 0.25s ease",
                            "&:hover": {
                              transform: "translateY(-4px)",
                              borderColor: withAlpha(stat.accent, 0.45),
                              boxShadow: `0 16px 36px ${withAlpha(
                                stat.accent,
                                0.16
                              )}`,
                            },
                          }}
                        >
                          <Stack
                            direction="row"
                            alignItems="center"
                            justifyContent="space-between"
                          >
                            <Typography
                              variant="h4"
                              fontWeight={800}
                              color="text.primary"
                            >
                              {stat.count}
                            </Typography>
                            <Box
                              sx={{
                                width: 40,
                                height: 40,
                                borderRadius: 2,
                                display: "grid",
                                placeItems: "center",
                                background: withAlpha(stat.accent, 0.12),
                                color: stat.accent,
                              }}
                            >
                              {stat.icon}
                            </Box>
                          </Stack>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mt: 0.5 }}
                          >
                            {stat.label}
                          </Typography>
                        </Box>
                      ))}
                    </Stack>
                  </Stack>
                </Box>
                {activeDefinition && (
                  <Box
                    key={activeDefinition.id}
                    sx={{
                      position: "relative",
                      animation:
                        "sectionReveal 700ms cubic-bezier(0.16, 1, 0.3, 1)",
                      "@keyframes sectionReveal": {
                        from: {
                          opacity: 0,
                          transform: "translateY(32px)",
                          filter: "blur(6px)",
                        },
                        to: {
                          opacity: 1,
                          transform: "translateY(0)",
                          filter: "blur(0)",
                        },
                      },
                    }}
                  >
                    {activeDefinition.id === "articles" ? (
                      <ArticlesShowcase
                        articles={articlesForShowcase}
                        accentColor={activeDefinition.accent}
                        buttonGradient={gradient}
                        search={articleSearch}
                        onSearchChange={handleArticleSearchChange}
                        loading={articlesLoading}
                        onOpenArticle={handleOpenArticle}
                        onToggleLike={handleToggleLike}
                        canLike={canLikeArticles}
                        likePendingId={likePendingId}
                        patientId={patientLikeId}
                        defaultAuthor={defaultAuthor}
                      />
                    ) : (
                      <DishesShowcase
                        dishes={dishes}
                        total={dishesTotal}
                        page={dishPage}
                        pageSize={DISH_PAGE_SIZE}
                        onPageChange={setDishPage}
                        search={dishSearch}
                        onSearchChange={handleDishSearchChange}
                        loading={dishesLoading}
                        onOpenDish={handleOpenDish}
                        accentColor={activeDefinition.accent}
                        buttonGradient={gradient}
                        buttonTextColor={textOnBrand}
                      />
                    )}
                  </Box>
                )}
              </Stack>
            </Grid>
          </Grid>
        )}
      </Container>
      <ArticleDialog
        open={articleDialogOpen}
        article={selectedArticle}
        loading={articleDialogLoading}
        onClose={handleCloseArticle}
        onToggleLike={handleToggleLike}
        canLike={canLikeArticles}
        likePendingId={likePendingId}
        patientId={patientLikeId}
        defaultAuthor={defaultAuthor}
      />
      <DishDialog
        open={dishDialogOpen}
        dish={selectedDish}
        loading={dishDetailLoading}
        onClose={handleCloseDish}
        gradient={gradient}
        accentColor={accentTertiary}
        textOnGradient={textOnBrand}
      />
    </Box>
  );
};

export default NutriologoProfile;
